import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FsErrorResponse, ProfileService } from '@fairandsmart/angular';
import { FsEventType } from '@fairandsmart/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError, map, mergeMap, take,
} from 'rxjs/operators';
import { AlertService } from '@Services/alert.service';
import { OrgaService } from '@Services/orga.service';
import { FsOrgState } from '../../../reducers';
import {
  CommonActionTypes,
  CoreEvent,
  LoadNotifications,
  LoadNotificationsFailure,
  LoadNotificationsSuccess,
} from './common.actions';

@Injectable()
export class CommonEffects {
  actions$ = inject(Actions);

  store = inject(Store<FsOrgState>);

  orgaService = inject(OrgaService);

  alert = inject(AlertService);

  dialog = inject(MatDialog);

  profile = inject(ProfileService);

  showTutorialNotification$ = createEffect(() => this.actions$.pipe(
    ofType(CommonActionTypes.ShowTutorialNotification),
    mergeMap(() => this.state$((state: FsOrgState) => {
      if (_.isEmpty(state.common.notifications)) {
        return of(new LoadNotifications());
      }
      return EMPTY;
    })),
  ));

  loadNotifications$ = createEffect(() => this.actions$.pipe(
    ofType(CommonActionTypes.LoadNotifications),
    mergeMap(() => this.profile.getParameter<{ [key: string]: boolean }>('notification.dismissed' as any)
      .pipe(
        map((response) => new LoadNotificationsSuccess({ response })),
        catchError((response: FsErrorResponse) => of(new LoadNotificationsFailure({ response }))),
      )),
  ));

  dismissNotifications$ = createEffect(() => this.actions$.pipe(
    ofType(CommonActionTypes.DismissTutorialNotification),
    mergeMap(() => this.state$((state: FsOrgState) => this.profile.setParameter({
      key: 'notification.dismissed' as any,
      reference: this.profile.id,
      values: state.common.notifications,
    }).pipe(
      mergeMap(() => EMPTY),
    ))),
  ));

  resetNotifications$ = createEffect(
() => this.actions$.pipe(
    ofType(CommonActionTypes.ResetTutorialNotification),
    mergeMap(() => this.profile.setParameter({
      key: 'notification.dismissed' as any,
      reference: this.profile.id,
      values: {},
    }).pipe(
      mergeMap(() => EMPTY),
    )),
  ),
    { dispatch: false },
  );

  organisationOffersChange$ = createEffect(
() => this.actions$.pipe(
    ofType(CommonActionTypes.CoreEvent),
    mergeMap((action: CoreEvent) => {
      switch (action.payload.event.eventType) {
        case FsEventType.ORGANISATION_OFFER_UPDATE: {
          return this.orgaService.loadOffers().pipe(
            mergeMap(() => EMPTY),
          );
        }
        case FsEventType.ORGANISATION_ROLE_ASSIGN: {
          if (action.payload.event.args?.member === this.profile.id) {
            // If role is assigned to connected member refresh its profile
            return this.profile.refreshProfile().pipe(
              mergeMap(() => EMPTY),
            );
          }
          return EMPTY;
        }
        case FsEventType.ORGANISATION_ROLE_CREATE:
        case FsEventType.ORGANISATION_ROLE_UPDATE:
        case FsEventType.ORGANISATION_ROLE_DELETE: {
          return this.orgaService.loadRoles().pipe(
            mergeMap(() => EMPTY),
          );
        }
        default:
          return EMPTY;
      }
    }),
  ),
    { dispatch: false },
  );

  private state$(project: (state: FsOrgState) => Observable<any>): Observable<any> {
    return this.store.pipe(
      take(1),
      mergeMap(project),
    );
  }
}
